// src/services/hostelService.js

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  arrayRemove,
} from 'firebase/firestore';
import {
  ref as storageRef, // Alias to avoid conflict with React's ref
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid'; // For unique image filenames
import { db, storage } from '../firebase'; // Your initialized Firebase app

const HOSTELS_COLLECTION = collection(db, 'hostels');

// Utility for creating image storage paths (can live here or be imported from a shared utils file)
const getHostelImageStoragePath = (hostelId, fileName) => {
  return `hostelImages/${hostelId}/${uuidv4()}_${fileName}`;
};

/**
 * Uploads an image file to Firebase Storage for a specific hostel.
 * @param {string} hostelId - The ID of the hostel.
 * @param {File} file - The image file to upload.
 * @returns {Promise<string>} - A promise that resolves with the download URL of the uploaded image.
 */
export async function uploadHostelImage(hostelId, file) {
  if (!hostelId) throw new Error('Hostel ID is required for image upload.');
  if (!file) throw new Error('File is required for image upload.');

  const filePath = getHostelImageStoragePath(hostelId, file.name);
  const imageRef = storageRef(storage, filePath);

  try {
    await uploadBytes(imageRef, file);
    const downloadUrl = await getDownloadURL(imageRef);
    return downloadUrl;
  } catch (error) {
    console.error("Error uploading image to Firebase Storage:", error);
    throw new Error(`Failed to upload image ${file.name}: ${error.message}`);
  }
}

/**
 * Deletes an image from Firebase Storage and removes its URL from the hostel's document in Firestore.
 * @param {string} hostelId - The ID of the hostel.
 * @param {string} imageUrl - The download URL of the image to delete.
 * @returns {Promise<void>}
 */
export async function deleteHostelImage(hostelId, imageUrl) {
  if (!hostelId) throw new Error('Hostel ID is required for image deletion.');
  if (!imageUrl) throw new Error('Image URL is required for deletion.');

  const imageFileRef = storageRef(storage, imageUrl); // Firebase SDK can create ref from download URL

  try {
    // Delete the file from Firebase Storage
    await deleteObject(imageFileRef);

    // Remove the image URL from the hostel's document in Firestore
    const hostelDocRef = doc(db, 'hostels', hostelId);
    await updateDoc(hostelDocRef, {
      images: arrayRemove(imageUrl), // Atomically removes the URL from the array
    });
  } catch (error) {
    console.error("Error deleting image from Firebase:", error);
    // Check if error is because object doesn't exist (it might have been already deleted)
    if (error.code === 'storage/object-not-found') {
      console.warn('Image not found in Storage, attempting to remove from Firestore array if present.');
      try {
        const hostelDocRef = doc(db, 'hostels', hostelId);
        await updateDoc(hostelDocRef, {
            images: arrayRemove(imageUrl)
        });
        return; // Successfully removed from array or was not there
      } catch (firestoreError) {
        console.error("Error removing image URL from Firestore after storage object not found:", firestoreError);
        throw new Error(`Failed to update hostel document after image storage error: ${firestoreError.message}`);
      }
    }
    throw new Error(`Failed to delete image: ${error.message}`);
  }
}

/**
 * Updates an existing hostel document in Firestore.
 * @param {string} hostelId - The ID of the hostel to update.
 * @param {object} hostelData - An object containing the hostel fields to update.
 * The 'images' field should be the complete, final array of image URLs.
 * @returns {Promise<object>} - A promise that resolves with the updated hostel data (passed data + ID).
 */
export async function updateHostel(hostelId, hostelData) {
  if (!hostelId) throw new Error('Hostel ID is required for update.');
  if (!hostelData) throw new Error('Hostel data is required for update.');

  const hostelDocRef = doc(db, 'hostels', hostelId);
  // Ensure 'id' is not part of the data payload sent to updateDoc
  const { id, ...dataToUpdate } = hostelData;

  try {
    await updateDoc(hostelDocRef, dataToUpdate);
    return { ...dataToUpdate, id: hostelId }; // Return the data that was successfully written, including the ID
  } catch (error) {
    console.error("Error updating hostel in Firestore:", error);
    throw new Error(`Failed to update hostel: ${error.message}`);
  }
}

/**
 * Creates a new hostel document in Firestore.
 * @param {object} hostelData - An object containing the initial hostel data.
 * The 'images' field should be the initial array of image URLs (can be empty).
 * An 'id' field should NOT be present as Firestore generates it.
 * @returns {Promise<object>} - A promise that resolves with the newly created hostel data, including its new ID.
 */
export async function createHostel(hostelData) {
  if (!hostelData) throw new Error('Hostel data is required for creation.');
  // Ensure 'id' is not part of the data payload for addDoc
  const { id, ...dataToCreate } = hostelData;

  try {
    const docRef = await addDoc(HOSTELS_COLLECTION, dataToCreate);
    return { ...dataToCreate, id: docRef.id }; // Return the data with the new ID
  } catch (error) {
    console.error("Error creating hostel in Firestore:", error);
    throw new Error(`Failed to create hostel: ${error.message}`);
  }
}

// You might also want a service function for deleting an entire hostel document
/**
 * Deletes an entire hostel document from Firestore.
 * Note: This function does NOT delete associated images from Storage.
 * Image deletion should be handled separately or before calling this if required.
 * (The AdminHostelsPage's onDelete handler already does this iteration).
 * @param {string} hostelId - The ID of the hostel to delete.
 * @returns {Promise<void>}
 */
export async function deleteHostelDocument(hostelId) {
  if (!hostelId) throw new Error('Hostel ID is required for deletion.');
  const hostelDocRef = doc(db, 'hostels', hostelId);
  try {
    await deleteDoc(hostelDocRef);
  } catch (error) {
    console.error("Error deleting hostel document:", error);
    throw new Error(`Failed to delete hostel document: ${error.message}`);
  }
}